import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useRef, useState } from "react";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { SafeReportResponse } from "../Conversion";
import {
  useDeleteEmailThread,
  useEmailThreads,
  useThisReportIsUpdating,
  useUploadEmailThread,
} from "../data";
import { useRerunningMessage } from "../RerunUI";
import { useOutlookDropHelper } from "../../../hooks/useOutlookDropHelper";
import { EmailAttachmentStagingDialog } from "./EmailAttachmentStagingDialog";
import styles from "./EmailCard.module.css";
import { EmailThreadModal, getThreadAttachmentCount } from "./EmailThreadModal";

const INBOUND_DOMAIN = "inbound.luminorrisk.com";

interface EmailCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

/** Extract a short subject from the first message of a thread. */
function getThreadSubject(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "(No subject)";
  return thread.messages[0]?.subject || "(No subject)";
}

/** Collect unique participant names/emails across all messages in a thread. */
function getThreadParticipants(thread: client.EmailThread): string {
  const participants = new Set<string>();
  for (const msg of thread.messages) {
    if (msg.from) participants.add(msg.from);
  }
  return Array.from(participants).join(", ");
}

/** Get the date of the latest message in a thread. */
function getLatestDate(thread: client.EmailThread): string {
  if (thread.messages.length === 0) return "";
  const last = thread.messages[thread.messages.length - 1];
  const dateStr = last?.date ?? "";
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export const EmailCard: React.FC<EmailCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const reportId = specialtyPropertyInfo.report_id;
  const emailThreads = specialtyPropertyInfo.report_json.email_threads ?? {};
  const hasThreads = Object.keys(emailThreads).length > 0;
  const { data, isLoading, isError } = useEmailThreads(reportId, hasThreads);
  const toast = useToast();

  const [selectedThread, setSelectedThread] =
    useState<client.EmailThread | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [stagedManifests, setStagedManifests] = useState<
    client.StagedEmailManifest[] | null
  >(null);
  const [reprocessTypes, setReprocessTypes] = useState<
    client.Attachment["original_mapping"][]
  >([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropBoxRef = useRef<HTMLDivElement>(null);
  const uploadMutation = useUploadEmailThread(reportId);
  const deleteMutation = useDeleteEmailThread(reportId);

  const isRerunningRemotely = useThisReportIsUpdating(reportId);
  const rerunningMessage = useRerunningMessage({
    update: isRerunningRemotely?.updating,
  });
  const uploadsDisabled = Boolean(isRerunningRemotely?.updating);

  const threads = data?.threads ?? [];

  const reportEmail = `sub+${reportId}@${INBOUND_DOMAIN}`;

  const submitFiles = useCallback(
    (files: File[]) => {
      const valid = files.filter((f) => {
        const name = f.name.toLowerCase();
        return name.endsWith(".eml") || name.endsWith(".msg");
      });

      if (valid.length === 0) {
        toast.show({
          severity: "warn",
          summary: "Unsupported file",
          detail: "Please select .eml or .msg files",
          life: 3000,
        });
        return;
      }

      uploadMutation.mutate(valid, {
        onSuccess: (result) => {
          if (!result.was_new) {
            toast.show({
              severity: "info",
              summary: "Already linked",
              detail: "This email thread is already attached to the report",
              life: 3000,
            });
          } else {
            toast.show({
              severity: "success",
              summary: "Thread linked",
              detail: `Added ${result.eml_count} email(s) to the report`,
              life: 3000,
            });
          }
          const withAttachments = result.staged_emails.filter(
            (manifest) => manifest.attachments.length > 0
          );
          if (withAttachments.length > 0) {
            setReprocessTypes(result.reprocess_classifications);
            setStagedManifests(withAttachments);
          }
        },
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Upload failed",
            detail: "Could not link email thread. Please try again.",
            life: 5000,
          });
        },
      });
    },
    [uploadMutation, toast]
  );

  // Lets users drag straight out of the new Outlook client, which the browser
  // alone cannot read. Inert unless the local helper app is installed.
  const { helperConnected, helperDragging } = useOutlookDropHelper({
    zoneRef: dropBoxRef,
    onFiles: submitFiles,
    enabled: !uploadsDisabled,
  });

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      submitFiles(Array.from(e.dataTransfer.files));
    },
    [submitFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        submitFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [submitFiles]
  );

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(reportEmail);
      toast.show({
        severity: "success",
        summary: "Copied",
        detail: "Email address copied to clipboard",
        life: 3000,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Copy failed",
        detail: "Could not copy to clipboard",
        life: 3000,
      });
    }
  };

  const handleRowClick = (thread: client.EmailThread) => {
    setSelectedThread(thread);
    setModalVisible(true);
  };

  const handleDeleteThread = useCallback(
    (thread: client.EmailThread) => {
      const subject = getThreadSubject(thread);
      // Attachments extracted from this thread ride on its conversation_id
      // and are removed with it; the user should see exactly what goes. The
      // .eml/.msg correspondence files are covered by the sentence below, so
      // this lists only the extracted documents.
      const extracted = (
        specialtyPropertyInfo.report_json.attachment_info?.attachments ?? []
      ).filter((a) => {
        if (a.conversation_id !== thread.thread_url) return false;
        const lower = a.name.toLowerCase();
        return !lower.endsWith(".eml") && !lower.endsWith(".msg");
      });
      const shownNames = extracted.slice(0, 5).map((a) => a.name);
      const moreCount = extracted.length - shownNames.length;
      confirmDialog({
        message: (
          <div className={styles.deleteConfirm}>
            <span>
              Remove "{subject}" from this report? Linked .eml files will be
              deleted. This cannot be undone.
            </span>
            {extracted.length > 0 && (
              <>
                <span>
                  {extracted.length === 1
                    ? "1 attachment extracted from this email will also be removed, and data parsed from it will be re-processed:"
                    : `${extracted.length} attachments extracted from this email will also be removed, and data parsed from them will be re-processed:`}
                </span>
                <ul className={styles.deleteConfirmList}>
                  {shownNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                  {moreCount > 0 && <li>…and {moreCount} more</li>}
                </ul>
              </>
            )}
          </div>
        ),
        header: "Delete Email Thread",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        accept: () => {
          deleteMutation.mutate(thread.thread_url, {
            onSuccess: () => {
              toast.show({
                severity: "success",
                summary: "Thread removed",
                detail: "Email thread deleted from the report",
                life: 3000,
              });
            },
            onError: () => {
              toast.show({
                severity: "error",
                summary: "Delete failed",
                detail: "Could not delete email thread. Please try again.",
                life: 5000,
              });
            },
          });
        },
      });
    },
    [deleteMutation, toast, specialtyPropertyInfo.report_json.attachment_info]
  );

  const copyButton = (
    <div className={styles.buttonContainer}>
      <Button
        label="Copy Email Address (for BCC)"
        icon="pi pi-copy"
        severity="secondary"
        raised
        text
        onClick={handleCopyEmail}
        tooltip={reportEmail}
        tooltipOptions={{ position: "bottom" }}
      />
    </div>
  );

  const dropBox = (
    <div
      ref={dropBoxRef}
      className={`${styles.dropBox} ${(isDragOver || helperDragging) && !uploadsDisabled ? styles.dropBoxActive : ""} ${uploadsDisabled ? styles.dropBoxDisabled : ""}`}
      onDragEnter={uploadsDisabled ? undefined : handleDragEnter}
      onDragLeave={uploadsDisabled ? undefined : handleDragLeave}
      onDragOver={uploadsDisabled ? undefined : handleDragOver}
      onDrop={uploadsDisabled ? undefined : handleDrop}
      onClick={
        uploadsDisabled ? undefined : () => fileInputRef.current?.click()
      }
      title={uploadsDisabled ? rerunningMessage : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".eml,.msg"
        multiple
        onChange={handleFileInput}
        className={styles.hiddenInput}
        disabled={uploadsDisabled}
      />
      {uploadsDisabled ? (
        <>
          <i className="pi pi-lock" />
          <span>{rerunningMessage}</span>
        </>
      ) : uploadMutation.isPending ? (
        <>
          <ProgressSpinner
            style={{ width: "24px", height: "24px" }}
            strokeWidth="4"
          />
          <span>Linking email thread...</span>
        </>
      ) : (
        <>
          <i className="pi pi-cloud-upload" />
          <span>
            Drag emails here or <strong>click to browse</strong> for .eml/.msg
            files
          </span>
          {helperConnected && (
            <span className={styles.helperHint}>
              <i className="pi pi-bolt" /> Outlook drag-and-drop enabled
            </span>
          )}
        </>
      )}
    </div>
  );

  const stagingDialog = (
    <EmailAttachmentStagingDialog
      reportId={reportId}
      manifests={stagedManifests ?? []}
      reprocessTypes={reprocessTypes}
      visible={stagedManifests !== null}
      onHide={() => setStagedManifests(null)}
    />
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className={styles.loadingContainer}>
        <ProgressSpinner
          style={{ width: "40px", height: "40px" }}
          strokeWidth="4"
        />
      </div>
    );
  } else if (isError) {
    body = (
      <div className={styles.emptyState}>
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "2rem", color: "var(--red-500)" }}
        />
        <span>Failed to load email threads. Please try again.</span>
      </div>
    );
  } else if (threads.length === 0) {
    body = (
      <div className={styles.emptyState}>
        <i className={`pi pi-envelope ${styles.emptyStateIcon}`} />
        <span>No email threads found for this report.</span>
        <span style={{ fontSize: "0.85rem" }}>
          CC or send emails to <strong>{reportEmail}</strong> to track threads
          here.
        </span>
      </div>
    );
  } else {
    body = (
      <div className={styles.container}>
        <DataTable
          value={threads}
          selectionMode="single"
          onRowClick={(e) => handleRowClick(e.data as client.EmailThread)}
          rowClassName={() => styles.threadRow}
          rowHover
          stripedRows
        >
          <Column
            header="Subject"
            body={(thread: client.EmailThread) => (
              <span className={styles.subjectColumn}>
                {getThreadSubject(thread)}
              </span>
            )}
            style={{ minWidth: "250px" }}
          />
          <Column
            header="Participants"
            body={(thread: client.EmailThread) => (
              <span className={styles.participantChip}>
                {getThreadParticipants(thread)}
              </span>
            )}
            style={{ minWidth: "200px" }}
          />
          <Column
            header="Date"
            body={(thread: client.EmailThread) => getLatestDate(thread)}
            style={{ width: "120px" }}
          />
          <Column
            header="Messages"
            body={(thread: client.EmailThread) => (
              <span className={styles.messageCount}>
                <i className="pi pi-comments" />
                {thread.messages.length}
              </span>
            )}
            style={{ width: "100px" }}
          />
          <Column
            header="Attachments"
            body={(thread: client.EmailThread) => {
              const count = getThreadAttachmentCount(thread.messages);
              if (count === 0) return null;
              return (
                <span className={styles.messageCount}>
                  <i className="pi pi-paperclip" />
                  {count}
                </span>
              );
            }}
            style={{ width: "120px" }}
          />
          <Column
            header=""
            body={(thread: client.EmailThread) => (
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                aria-label="Delete email thread"
                tooltip="Delete thread"
                tooltipOptions={{ position: "left" }}
                disabled={
                  deleteMutation.isPending &&
                  deleteMutation.variables === thread.thread_url
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteThread(thread);
                }}
              />
            )}
            style={{ width: "60px" }}
          />
        </DataTable>
      </div>
    );
  }

  return (
    <Card title="Emails" style={{ position: "relative" }}>
      {copyButton}
      {dropBox}
      {stagingDialog}
      {body}
      <EmailThreadModal
        visible={modalVisible}
        onHide={() => {
          setModalVisible(false);
          setSelectedThread(null);
        }}
        thread={selectedThread}
        reportId={reportId}
      />
    </Card>
  );
};
